/* ==========================================================================
   Baby Store — comportamiento compartido de las secciones "baby-*"
   Vanilla JS, sin dependencias. Compatible con el editor de Shopify.
   Módulos: reveal, accordion, slider, rotator, header (drawer/sticky/search).
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function markInit(el, key) {
    if (el.getAttribute(key) === 'true') return false;
    el.setAttribute(key, 'true');
    return true;
  }

  /* ----------------------------------------------------------------------
     1. Reveal al entrar al viewport
     ---------------------------------------------------------------------- */
  function initReveal(scope) {
    var items = scope.querySelectorAll('.baby-reveal');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      each(items, function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    each(items, function (el, i) {
      if (!markInit(el, 'data-baby-reveal-init')) return;
      el.style.transitionDelay = Math.min(i, 6) * 55 + 'ms';
      observer.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     2. Accordion accesible (FAQ)
     ---------------------------------------------------------------------- */
  function initAccordions(scope) {
    each(scope.querySelectorAll('[data-baby-accordion]'), function (root) {
      if (!markInit(root, 'data-baby-accordion-init')) return;

      var single = root.getAttribute('data-single-open') === 'true';
      var triggers = root.querySelectorAll('[data-baby-accordion-trigger]');

      function close(trigger) {
        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        trigger.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
        var item = trigger.closest('[data-baby-accordion-item]');
        if (item) item.classList.remove('is-open');
      }

      function open(trigger) {
        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        trigger.setAttribute('aria-expanded', 'true');
        if (panel) panel.hidden = false;
        var item = trigger.closest('[data-baby-accordion-item]');
        if (item) item.classList.add('is-open');
      }

      each(triggers, function (trigger, index) {
        trigger.addEventListener('click', function () {
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (single) {
            each(triggers, function (other) {
              if (other !== trigger) close(other);
            });
          }
          if (isOpen) {
            close(trigger);
          } else {
            open(trigger);
          }
        });

        trigger.addEventListener('keydown', function (event) {
          var key = event.key;
          var next = null;
          if (key === 'ArrowDown') next = triggers[index + 1] || triggers[0];
          else if (key === 'ArrowUp') next = triggers[index - 1] || triggers[triggers.length - 1];
          else if (key === 'Home') next = triggers[0];
          else if (key === 'End') next = triggers[triggers.length - 1];
          if (next) {
            event.preventDefault();
            next.focus();
          }
        });
      });
    });
  }

  /* ----------------------------------------------------------------------
     3. Slider por scroll-snap (testimonios / tarjetas en móvil)
     ---------------------------------------------------------------------- */
  function initSliders(scope) {
    each(scope.querySelectorAll('[data-baby-slider]'), function (root) {
      if (!markInit(root, 'data-baby-slider-init')) return;

      var track = root.querySelector('[data-baby-slider-track]');
      if (!track) return;

      var slides = track.querySelectorAll('[data-baby-slide]');
      var dotsWrap = root.querySelector('[data-baby-slider-dots]');
      var prevBtn = root.querySelector('[data-baby-slider-prev]');
      var nextBtn = root.querySelector('[data-baby-slider-next]');
      var autoplay = parseInt(root.getAttribute('data-autoplay') || '0', 10);
      var timer = null;
      var paused = false;

      if (slides.length < 2) {
        if (dotsWrap) dotsWrap.hidden = true;
        if (prevBtn) prevBtn.hidden = true;
        if (nextBtn) nextBtn.hidden = true;
        return;
      }

      /* --- puntos --- */
      var dots = [];
      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        each(slides, function (slide, i) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'baby-dots__btn';
          btn.setAttribute('role', 'tab');
          btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
          btn.setAttribute(
            'aria-label',
            (dotsWrap.getAttribute('data-label') || 'Ir al elemento') + ' ' + (i + 1)
          );
          btn.addEventListener('click', function () {
            goTo(i);
            pause();
          });
          dotsWrap.appendChild(btn);
          dots.push(btn);
        });
        dotsWrap.setAttribute('role', 'tablist');
      }

      function visibleCount() {
        var slideWidth = slides[0].getBoundingClientRect().width;
        if (!slideWidth) return 1;
        return Math.max(1, Math.round(track.clientWidth / slideWidth));
      }

      function currentIndex() {
        var slideWidth = slides[0].getBoundingClientRect().width;
        var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
        if (!slideWidth) return 0;
        return Math.round(track.scrollLeft / (slideWidth + gap));
      }

      function maxIndex() {
        return Math.max(0, slides.length - visibleCount());
      }

      function goTo(index) {
        var bounded = Math.max(0, Math.min(index, maxIndex()));
        var target = slides[bounded];
        if (!target) return;
        track.scrollTo({
          left: target.offsetLeft - track.offsetLeft,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }

      function sync() {
        var index = currentIndex();
        dots.forEach(function (dot, i) {
          dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
        if (prevBtn) prevBtn.disabled = index <= 0;
        if (nextBtn) nextBtn.disabled = index >= maxIndex();
      }

      var scrollTimer = null;
      track.addEventListener(
        'scroll',
        function () {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(sync, 80);
        },
        { passive: true }
      );

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          goTo(currentIndex() - 1);
          pause();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          goTo(currentIndex() + 1);
          pause();
        });
      }

      track.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          goTo(currentIndex() + 1);
          pause();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goTo(currentIndex() - 1);
          pause();
        }
      });

      /* --- autoplay con pausa al interactuar --- */
      function pause() {
        paused = true;
        window.clearInterval(timer);
      }

      if (autoplay > 0 && !reduceMotion) {
        timer = window.setInterval(function () {
          if (paused || document.hidden) return;
          var next = currentIndex() + 1;
          goTo(next > maxIndex() ? 0 : next);
        }, autoplay);

        ['pointerdown', 'focusin', 'touchstart', 'wheel'].forEach(function (evt) {
          root.addEventListener(evt, pause, { passive: true, once: true });
        });

        root.addEventListener('mouseenter', function () {
          paused = true;
        });
        root.addEventListener('mouseleave', function () {
          if (timer) paused = false;
        });
      }

      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  /* ----------------------------------------------------------------------
     4. Rotador de mensajes (barra de anuncios en móvil)
     ---------------------------------------------------------------------- */
  function initRotators(scope) {
    each(scope.querySelectorAll('[data-baby-rotator]'), function (root) {
      if (!markInit(root, 'data-baby-rotator-init')) return;

      var items = root.querySelectorAll('[data-baby-rotator-item]');
      if (items.length < 2 || reduceMotion) return;

      var delay = parseInt(root.getAttribute('data-baby-rotator') || '4500', 10);
      var index = 0;
      var mq = window.matchMedia('(max-width: 767px)');

      function apply() {
        each(items, function (item, i) {
          item.classList.toggle('is-current', i === index);
        });
      }

      window.setInterval(function () {
        if (!mq.matches || document.hidden) return;
        index = (index + 1) % items.length;
        apply();
      }, delay);

      apply();
    });
  }

  /* ----------------------------------------------------------------------
     5. Header: drawer móvil, submenús, buscador y sticky
     ---------------------------------------------------------------------- */
  function initHeader(scope) {
    each(scope.querySelectorAll('[data-baby-header]'), function (header) {
      if (!markInit(header, 'data-baby-header-init')) return;

      var drawer = header.querySelector('[data-baby-drawer]');
      var openBtn = header.querySelector('[data-baby-drawer-open]');
      var closeEls = header.querySelectorAll('[data-baby-drawer-close]');
      var lastFocused = null;

      function focusables() {
        return drawer
          ? drawer.querySelectorAll(
              'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea'
            )
          : [];
      }

      function openDrawer() {
        if (!drawer) return;
        lastFocused = document.activeElement;
        drawer.classList.add('is-open');
        drawer.removeAttribute('hidden');
        document.documentElement.classList.add('baby-drawer-open');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
        var first = focusables()[0];
        if (first) first.focus();
      }

      function closeDrawer() {
        if (!drawer) return;
        drawer.classList.remove('is-open');
        document.documentElement.classList.remove('baby-drawer-open');
        if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
        window.setTimeout(function () {
          if (!drawer.classList.contains('is-open')) drawer.setAttribute('hidden', '');
        }, 250);
        if (lastFocused && lastFocused.focus) lastFocused.focus();
      }

      if (openBtn) openBtn.addEventListener('click', openDrawer);
      each(closeEls, function (el) {
        el.addEventListener('click', closeDrawer);
      });

      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape' || !drawer || !drawer.classList.contains('is-open')) return;
        closeDrawer();
      });

      if (drawer) {
        drawer.addEventListener('keydown', function (event) {
          if (event.key !== 'Tab') return;
          var list = focusables();
          if (!list.length) return;
          var first = list[0];
          var last = list[list.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        });
      }

      /* Submenús (móvil + teclado en escritorio) */
      each(header.querySelectorAll('[data-baby-submenu-toggle]'), function (toggle) {
        toggle.addEventListener('click', function () {
          var expanded = toggle.getAttribute('aria-expanded') === 'true';
          var parent = toggle.closest('[data-baby-has-submenu]');
          toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          if (parent) parent.classList.toggle('is-open', !expanded);
        });
      });

      /* Cierra submenús de escritorio al salir con el teclado */
      each(header.querySelectorAll('[data-baby-has-submenu]'), function (parent) {
        parent.addEventListener('focusout', function (event) {
          if (parent.contains(event.relatedTarget)) return;
          parent.classList.remove('is-open');
          var toggle = parent.querySelector('[data-baby-submenu-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        });
      });

      /* Buscador compacto en móvil */
      var searchToggle = header.querySelector('[data-baby-search-toggle]');
      var searchBox = header.querySelector('[data-baby-search]');
      if (searchToggle && searchBox) {
        searchToggle.addEventListener('click', function () {
          var open = searchBox.classList.toggle('is-open');
          searchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open) {
            var input = searchBox.querySelector('input[type="search"]');
            if (input) input.focus();
          }
        });
      }

      /* Sticky */
      if (header.getAttribute('data-sticky') === 'true') {
        var sentinel = document.createElement('div');
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;';
        header.parentNode.insertBefore(sentinel, header);

        if ('IntersectionObserver' in window) {
          new IntersectionObserver(
            function (entries) {
              header.classList.toggle('is-stuck', !entries[0].isIntersecting);
            },
            { threshold: 0 }
          ).observe(sentinel);
        }
      }
    });
  }

  /* ----------------------------------------------------------------------
     6. Contador del carrito: oculta la burbuja cuando llega a 0
     El theme (ella.min.js) actualiza el texto de [data-cart-count] por AJAX,
     asi que observamos el nodo en vez de duplicar la logica del carrito.
     ---------------------------------------------------------------------- */
  function initCartCount(scope) {
    if (!('MutationObserver' in window)) return;

    each(scope.querySelectorAll('[data-baby-cart-count]'), function (node) {
      if (!markInit(node, 'data-baby-count-init')) return;

      function sync() {
        var value = parseInt((node.textContent || '').replace(/\D/g, ''), 10) || 0;
        node.classList.toggle('is-empty', value === 0);
      }

      new MutationObserver(sync).observe(node, { childList: true, characterData: true, subtree: true });
      sync();
    });
  }

  /* ----------------------------------------------------------------------
     Bootstrap
     ---------------------------------------------------------------------- */
  function initAll(scope) {
    var root = scope || document;
    initReveal(root);
    initAccordions(root);
    initSliders(root);
    initRotators(root);
    initHeader(root);
    initCartCount(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* Editor de Shopify: re-inicializar la sección recargada */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    document.documentElement.classList.remove('baby-drawer-open');
  });

  window.BabyStore = { init: initAll };
})();
